import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  EntitlementSlugEnum,
  InternalType,
} from '@electricjs/core_entity-client';

import { NotificationComponent, NotificationsComponentProps } from '@common';
import { SmartSearchPageNavigationOption } from '@/components/SmartSearch';

import { getInternalPages } from '@/constants/smartSearch/internalPages';
import { getSearchableActions } from '@/constants/smartSearch/searchableActions';
import { knowledgeBaseUrl } from '@/constants/urls';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useNotificationsProps } from '@/hooks/useNotificationsProps';
import { useLogout } from '@/hooks/useLogout';
import { useAppSelector } from '@/redux/store';
import { OrganizationUserRoleSlugs } from '@/types/users';

import {
  Flex,
  IconButton,
  Text,
  Tooltip,
  spaces,
  useBreakpoint,
} from '@electricjs/arc';
import formatName from '@/helpers/formatName';
import { SmartSearchSelectOption } from '../../SmartSearch';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import generateMenuItems from './generateMenuItems';
import { useEffect, useMemo } from 'react';
import GlobalSearch from '../GlobalSearch';
import { TopNav } from './TopNavBase';

import { usePageTitleContext } from '@/contexts/PageTitleContext';

import electricLogo from '@/assets/electric_logo_red.svg';
import electricMobileLogo from '@/assets/electric_bolt_only_logo_red.svg';
import { useGetMyRequestsQuery } from '@/redux/slices/requestApiSlice';
import { RequestStatus } from '@/types/requests';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import { useGetOrganizationsQuery } from '@/redux/slices/organizationApiSlice';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';

type TopBarWrapperProps = {
  onMenuClick?: () => void;
};

// Get static searchable data (pages and actions, excluding employees)
const getStaticSearchableData = (
  isAdmin: boolean,
  hasElectricAppEntitlement: boolean
): SmartSearchSelectOption[] => {
  const internalPages = getInternalPages();

  const filteredPages = internalPages.filter(page => {
    if (
      (page as SmartSearchPageNavigationOption).text ===
      i18n.t('common:constants.smartSearch.pages.devices.text')
    ) {
      return hasElectricAppEntitlement;
    }
    return true;
  }) as SmartSearchSelectOption[];

  const staticData = [...filteredPages, ...getSearchableActions()];

  // Filter static data based on user role
  if (isAdmin) {
    return staticData;
  } else {
    return filteredPages.filter(page => page.isVisibleToEndUser === true);
  }
};

const MenuDetailSection = ({
  organizationName,
  employeeName,
  employeeEmail,
}: {
  organizationName: string;
  employeeName: string;
  employeeEmail: string;
}) => (
  <Flex p="0 1.6rem" flexDirection="column">
    <Text variant="label-large">{organizationName}</Text>
    <Text variant="body-2">{employeeName}</Text>
    <Text variant="legal">{employeeEmail}</Text>
  </Flex>
);

const TopBarWrapper = ({ onMenuClick }: TopBarWrapperProps) => {
  const logout = useLogout();
  const { openModal, closeModal } = useGlobalUI();
  const { md } = useBreakpoint();
  const { title: pageTitle, leftSideAfterTitleContent: PageTitleLeftContent } =
    usePageTitleContext();
  const { t, i18n } = useTranslation(['components', 'common']);
  const loggedUser = useAppSelector(state => state.loggedUser);
  const hasElectricPermissions = !!loggedUser?.electricPermissions;
  const organizationName = loggedUser?.organizationName ?? '';
  const employeeName = formatName({
    firstName: loggedUser?.employeeFirstName,
    lastName: loggedUser?.employeeLastName,
  });
  const employeeEmail = loggedUser?.email ?? '';
  const allUserOrganizationRoles = loggedUser?.allUserOrganizationRoles;
  const requiresOrgDisambiguationHeader =
    loggedUser?.requiresOrgDisambiguationHeader;
  const isAdminOrSupportRole = loggedUser?.organizationUserRoles?.some(
    role =>
      role.slug === OrganizationUserRoleSlugs.SUPER_ADMIN ||
      role.slug === OrganizationUserRoleSlugs.PRODUCT_SUPPORT
  );
  const isDemoOrg = loggedUser?.organizationInternalType === InternalType.Demo;
  const hasElectricAppEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.DesktopApp
  );

  const { itHubMyOrganizationsSwitcher, itHubI18N } = useFlags();

  // Internal organization switcher should only show if:
  // 1. User has internal electric permissions
  // 2. User has the FF and roles in more than 1 organization
  const showOrganizationSwitcher =
    hasElectricPermissions ||
    (itHubMyOrganizationsSwitcher &&
      allUserOrganizationRoles !== null &&
      allUserOrganizationRoles !== undefined &&
      Object.keys(allUserOrganizationRoles)?.length > 1);

  const organizationId = useGetOrganizationId();

  // We're fetching a limit of 0 queries because we only care about the total
  const { data: myRequestsData } = useGetMyRequestsQuery({
    organizationId,
    limit: 0,
    status: RequestStatus.InProgress,
  });
  const requestCount = myRequestsData?.total ?? 0;

  const notificationsProps: NotificationsComponentProps =
    useNotificationsProps(organizationId);

  // Get static searchable data (pages and actions) filtered by user role
  const searchableData = useMemo(
    () =>
      getStaticSearchableData(
        !!isAdminOrSupportRole,
        hasElectricAppEntitlement
      ),
    // i18n.language is intentionally included so the static searchable data
    // (page titles, action labels) is recomputed when the language changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdminOrSupportRole, hasElectricAppEntitlement, i18n.language]
  );

  const {
    data: myFirstTenOrganizations,
    isError: isGetOrganizationsError,
    isFetching: isGetOrganizationsFetching,
  } = useGetOrganizationsQuery({ limit: 10 });

  const menuItems = generateMenuItems({
    isAdminRole: isAdminOrSupportRole,
    isDemoOrg: isDemoOrg,
    showOrganizationSwitcher,
    showLanguageSwitcher: itHubI18N,
    hasElectricAppEntitlement,
    hasElectricPermissions,
    organizationId,
    myOrganizations: myFirstTenOrganizations,
    myOrgRoles: allUserOrganizationRoles,
    isFetchingMyOrganizations: isGetOrganizationsFetching,
    isErrorMyOrganizations: isGetOrganizationsError,
    requiresOrgDisambiguationHeader,
    t,
  });

  // Monitor "cmd/ctrl + k" key press to open the global search modal
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (searchableData && e.code === 'KeyK' && (e.metaKey || e.ctrlKey)) {
        openModal(
          <GlobalSearch
            searchableData={searchableData}
            isAdmin={!!isAdminOrSupportRole}
            organizationId={organizationId}
            onCloseModal={closeModal}
          />
        );
      }

      // Close modal on escape key press
      // NOTE: this will affect all modals, not just the global search modal
      if (e.code === 'Escape') {
        closeModal();
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return function cleanup() {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    searchableData,
    openModal,
    closeModal,
    isAdminOrSupportRole,
    organizationId,
  ]);

  const pageTitleElement =
    md && pageTitle ? (
      <Flex alignItems="center" gap={spaces.space_s} overflow="hidden">
        <Text
          variant="heading-2"
          aria-hidden="true"
          style={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}>
          {pageTitle}
        </Text>
        {PageTitleLeftContent ? <PageTitleLeftContent /> : null}
      </Flex>
    ) : undefined;

  return (
    <TopNav
      logoProps={{
        logoElement: md ? (
          <img alt="Electric" height="42" src={electricLogo} />
        ) : (
          <img alt="Electric" height="42" src={electricMobileLogo} />
        ),
        logoLinkProps: {
          href: '/',
        },
      }}
      logoutOnClick={logout}
      menuItems={menuItems}
      navMenuName={employeeName?.length ? employeeName : organizationName}
      onMobileMenuClick={onMenuClick}
      knowledgeBaseURL={knowledgeBaseUrl}
      notificationsElement={<NotificationComponent {...notificationsProps} />}
      {...(searchableData
        ? {
            searchElement: (
              // TODO: use os detection to determine the correct key for display
              <Tooltip content={t('common:topNav.search')} placement="bottom">
                <IconButton
                  id="launch-global-search-button"
                  icon="search"
                  iconSize="medium"
                  onClick={() =>
                    openModal(
                      <GlobalSearch
                        searchableData={searchableData}
                        isAdmin={!!isAdminOrSupportRole}
                        organizationId={organizationId}
                        onCloseModal={closeModal}
                      />
                    )
                  }
                />
              </Tooltip>
            ),
          }
        : {})}
      menuDetailSection={
        <MenuDetailSection
          organizationName={organizationName}
          employeeName={employeeName}
          employeeEmail={employeeEmail}
        />
      }
      taskCount={requestCount}
      pageTitleElement={pageTitleElement}
    />
  );
};

export default TopBarWrapper;
